import { useInfiniteQuery } from 'react-query';
import {
  PaymentPlanListResponse,
  PaymentPlanEdge,
  getPaymentPlanList,
  PaymentPlanModel,
  PaymentPlanSummary,
} from '@frontend/api-payment-plans';
import { useGraphQLQuery } from '@frontend/api-payments';
import { useMerchants } from '@frontend/payments-hooks';
import { useScopedQuery } from '@frontend/scope';
import { usePaymentPlanShallowStore } from '../providers/payment-plans-provider';

export const useQueryPersonPaymentPlans = (personId?: string) => {
  const { data, isLoading } = useScopedQuery<PaymentPlanListResponse, Error>({
    enabled: !!personId,
    queryKey: ['person-payment-plans', personId],
    queryFn: () => getPaymentPlanList({ search: { text: personId } }),
  });

  return {
    personPaymentPlans: data?.paymentPlan?.edges?.map((e) => e.node) ?? [],
    summary: data?.summary,
    personPaymentPlansLoading: isLoading,
  };
};

export const useQueryAllPaymentPlans = () => {
  const { filter, order, numRows, pageParams } = usePaymentPlanShallowStore('filter', 'order', 'numRows', 'pageParams');

  // Calculate total records fetched so far (numRows * currentPage)
  const totalRecordsFetched = numRows * Math.max(pageParams.currentPage, 1);

  const { data, isLoading } = useScopedQuery<PaymentPlanListResponse, Error>({
    queryKey: ['print-payment-plans', filter, order, numRows, pageParams.currentPage],
    queryFn: () => getPaymentPlanList({ ...filter, order, first: totalRecordsFetched }),
  });

  return {
    allPaymentPlans: data?.paymentPlan?.edges?.map((e) => e.node) ?? [],
    summary: data?.summary,
    allPaymentPlansLoading: isLoading,
  };
};

const usePaymentPlansSearchQuery = () => {
  const { allMerchantsLoaded } = useMerchants('selectedLocationIds');

  const { filter, numRows, order, pageParams, setPageParams } = usePaymentPlanShallowStore(
    'filter',
    'numRows',
    'order',
    'pageParams',
    'setPageParams'
  );

  const { data: paymentPlans, ...rest } = useGraphQLQuery<
    PaymentPlanListResponse,
    PaymentPlanModel,
    PaymentPlanSummary,
    PaymentPlanEdge
  >({
    queryKey: ['payment-plans', filter, numRows, order],
    queryFn: (pageParam) => getPaymentPlanList({ ...filter, ...pageParam, order }),
    numRows,
    pageParams,
    setPageParams,
    getPageInfo: (response?: PaymentPlanListResponse) => response?.pageInfo,
    getEdges: (response?: PaymentPlanListResponse) => response?.paymentPlan?.edges,
    getSummary: (response?: PaymentPlanListResponse) => response?.summary,
    enabled: !!allMerchantsLoaded,
  });

  return {
    paymentPlans,
    ...rest,
  };
};
export const usePaymentPlanSearch = usePaymentPlansSearchQuery;

export const usePaymentPlansSearchInfiniteQuery = () => {
  const { allMerchantsLoaded } = useMerchants('selectedLocationIds');

  const { filter, numRows, order } = usePaymentPlanShallowStore('filter', 'numRows', 'order');

  return useInfiniteQuery({
    queryKey: ['payment-plans', filter, numRows, order],
    queryFn: ({ pageParam }) => getPaymentPlanList({ ...filter, after: pageParam, order }),
    getNextPageParam: (lastPage) => lastPage.pageInfo?.endCursor,
    enabled: !!allMerchantsLoaded,
    select: (data) => {
      const pagesData = data.pages.flatMap((page) => page.paymentPlan?.edges?.map((edge) => edge.node) ?? []);
      return {
        pages: pagesData,
        pageParams: data.pageParams,
      };
    },
  });
};
